import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {ConfigProvider} from 'antd';
import {Fade} from 'react-awesome-reveal';
import {Helmet} from 'react-helmet';
import {useTranslation} from 'react-i18next';
import {
    BarChartOutlined,
    CheckCircleOutlined,
    FileTextOutlined,
    RightOutlined,
    SolutionOutlined,
    TeamOutlined,
    UserOutlined,
} from '@ant-design/icons';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import classes from './courses.module.css';
import IntlMessage from '../../components/IntlMessage/IntlMessage';
import BlockLoading from '../../components/LoadingComponents/BlockLoading';
import {getMarketCourses, selectVisibleCourses} from '../../store/slices/MarketCoursesSlice';
import {CATEGORY_BY_CODE, FORMAT_BY_CODE, LEVEL_BY_CODE, AUDIENCE_BY_CODE} from './courses.enums';
import useLocalizedText from './hooks/useLocalizedText';
import useReducedMotion from './hooks/useReducedMotion';
import SectionTitle from './components/SectionTitle';
import SyllabusTable from './components/SyllabusTable';
import EnrollCard, {MobileEnrollBar} from './components/EnrollCard';
import CtaStrip from './components/CtaStrip';
import HowItWorks from './components/HowItWorks';
import CourseFaq from './components/CourseFaq';
import CourseApplyForm from './components/CourseApplyForm';
import CourseCard from './components/CourseCard';
import CourseNotFound from './components/CourseNotFound';
import CertificatePreview from './components/CertificatePreview';
import {durationLabel, hoursLabelFull, pluralRu} from './courses.utils';
import {trainerPhoto} from './img/trainers';
import {selectCatalogPath} from '../../store/slices/CoursesCatalogSlice';

const ROLE_ICONS = [TeamOutlined, UserOutlined, SolutionOutlined, BarChartOutlined];

/** Показывать ли блок «Частые вопросы о курсе». Заказчик попросил убрать его со страницы. */
const SHOW_COURSE_FAQ = false;

/** Показывать ли блок «Что вы унесёте с курса» с макетом документа. Тоже скрыт по просьбе заказчика. */
const SHOW_COURSE_RESULT = false;

const CoursePage = () => {
    const {slug} = useParams();
    const {t} = useTranslation();
    const pick = useLocalizedText();
    const reduced = useReducedMotion();

    const dispatch = useDispatch();
    const allCourses = useSelector(selectVisibleCourses);
    const {isLoading, isLoaded} = useSelector((state) => state.marketCourses);

    // Список нужен целиком: из него же собирается блок «Похожие программы»,
    // поэтому отдельного запроса на одну карточку нет
    useEffect(() => {
        if (!isLoaded) dispatch(getMarketCourses());
    }, [dispatch, isLoaded]);

    // Курс ищем по слагу из URL, а не берём из location.state: иначе прямой заход
    // по ссылке и обновление страницы дают белый экран — так уже сломана деталка
    // микроквалификаций.
    const course = allCourses.find((c) => c.slug === slug);

    const enrollRef = useRef(null);
    const applyRef = useRef(null);

    const [progress, setProgress] = useState(0);
    const [barVisible, setBarVisible] = useState(false);

    // Путь возврата в каталог с сохранённым отбором — его пишет CoursesListPage
    const catalogPath = useSelector(selectCatalogPath);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    useEffect(() => {
        if (reduced) return undefined;
        const onScroll = () => {
            const doc = document.documentElement;
            const max = doc.scrollHeight - doc.clientHeight;
            setProgress(max > 0 ? Math.min(1, doc.scrollTop / max) : 0);
        };
        onScroll();
        window.addEventListener('scroll', onScroll, {passive: true});
        return () => window.removeEventListener('scroll', onScroll);
    }, [reduced, course]);

    // Нижний бар появляется, когда карточка записи ушла из вида, и обязан прятаться,
    // когда во вьюпорт вошла форма — иначе он перекроет поля, в которые печатают.
    useEffect(() => {
        if (!course || typeof IntersectionObserver === 'undefined') return undefined;
        let cardVisible = true;
        let formVisible = false;
        const apply = () => setBarVisible(!cardVisible && !formVisible);

        const cardObserver = new IntersectionObserver(([e]) => {
            cardVisible = e.isIntersecting;
            apply();
        }, {threshold: 0});
        const formObserver = new IntersectionObserver(([e]) => {
            formVisible = e.isIntersecting;
            apply();
        }, {threshold: 0});

        if (enrollRef.current) cardObserver.observe(enrollRef.current);
        if (applyRef.current) formObserver.observe(applyRef.current);
        return () => {
            cardObserver.disconnect();
            formObserver.disconnect();
        };
    }, [course]);

    const related = useMemo(() => {
        if (!course) return [];
        const sameCategory = allCourses.filter(
            (c) => c.slug !== course.slug && c.category === course.category && c.visible !== false,
        );
        const rest = allCourses.filter(
            (c) => c.slug !== course.slug && c.category !== course.category && c.visible !== false,
        );
        return [...sameCategory, ...rest].slice(0, 3);
    }, [course, allCourses]);

    const scrollToForm = () => {
        applyRef.current?.scrollIntoView({behavior: reduced ? 'auto' : 'smooth', block: 'start'});
    };

    // key вынесен из props: он нужен и обёртке Fade, и Fragment'у в режиме без анимаций,
    // иначе React ругается на список без ключей
    const reveal = (node, {key, ...props} = {}) => (reduced
        ? <React.Fragment key={key}>{node}</React.Fragment>
        : <Fade key={key} triggerOnce duration={500} {...props}>{node}</Fade>);

    // Пока список не пришёл, курса в нём нет — но это не «курс не найден»,
    // поэтому сначала показываем загрузку и только потом сообщение об отсутствии
    const isFirstLoad = isLoading && !isLoaded;

    const body = isFirstLoad ? (
        <div className={classes.notFound}><BlockLoading/></div>
    ) : !course ? <CourseNotFound/> : (() => {
        const category = CATEGORY_BY_CODE[course.category];
        const format = FORMAT_BY_CODE[course.format];
        const level = LEVEL_BY_CODE[course.level];
        const photo = trainerPhoto(course.trainer?.photo);
        const faqItems = (course.faq || []).map((f) => ({q: pick(f.q), a: pick(f.a)}));

        return (
            <>
                <section className={classes.courseHero}>
                    <div className={classes.courseHeroInner}>
                        <div className={classes.courseHeroLeft}>
                            <nav className={classes.crumbs}>
                                <Link to="/" className={classes.crumbLink}>
                                    <IntlMessage id="coursesPage.course.breadcrumbHome"/>
                                </Link>
                                <RightOutlined style={{fontSize: 10}}/>
                                {/* Ведёт в каталог с сохранённым отбором, страницей и поиском:
                                    раньше крошка возвращала на первую страницу с нуля,
                                    хотя кнопка «назад» в браузере состояние сохраняла. */}
                                <Link to={catalogPath} className={classes.crumbLink}>
                                    <IntlMessage id="coursesPage.course.breadcrumbList"/>
                                </Link>
                                <RightOutlined style={{fontSize: 10}}/>
                                <span className={classes.crumbCurrent}>{pick(course.title)}</span>
                            </nav>

                            {category && (
                                <span className={classes.heroTag}>
                                    <IntlMessage id={category.labelKey}/>
                                </span>
                            )}
                            <h1 className={classes.courseTitle}>{pick(course.title)}</h1>
                            <p className={classes.courseSubtitle}>{pick(course.subtitle)}</p>

                            <div className={classes.metaTiles}>
                                <div className={classes.metaTile}>
                                    <span className={classes.metaLabel}>
                                        <IntlMessage id="coursesPage.course.meta.format"/>
                                    </span>
                                    <span className={classes.metaValue}>
                                        {format && <IntlMessage id={format.labelKey}/>}
                                    </span>
                                </div>
                                <div className={classes.metaTile}>
                                    <span className={classes.metaLabel}>
                                        <IntlMessage id="coursesPage.course.meta.duration"/>
                                    </span>
                                    <span className={classes.metaValue}>
                                        {durationLabel(course.durationHours, pick(course.durationShape))}
                                    </span>
                                </div>
                                <div className={classes.metaTile}>
                                    <span className={classes.metaLabel}>
                                        <IntlMessage id="coursesPage.course.meta.level"/>
                                    </span>
                                    <span className={classes.metaValue}>
                                        {level && <IntlMessage id={level.labelKey}/>}
                                    </span>
                                </div>
                                <div className={classes.metaTile}>
                                    <span className={classes.metaLabel}>
                                        <IntlMessage id="coursesPage.course.meta.result"/>
                                    </span>
                                    <span className={classes.metaValue}>{pick(course.finalProduct)}</span>
                                </div>
                            </div>
                        </div>

                        <aside className={classes.enrollCol} ref={enrollRef}>
                            <EnrollCard course={course} onApply={scrollToForm} onDownload={scrollToForm}/>
                        </aside>
                    </div>
                </section>

                {/* Цель и результаты */}
                <section className={classes.section}>
                    <div className={classes.container}>
                        <SectionTitle id="coursesPage.course.goalTitle" className={classes.sectionHead} as="h2"/>
                        <p className={classes.goalText}>{pick(course.goal)}</p>
                        {/* блок скрыт, если в документе курса результатов нет */}
                        {(course.outcomes || []).length > 0 && (
                            <>
                                <h3 className={classes.subheading}>
                                    <IntlMessage id="coursesPage.course.outcomesTitle"/>
                                </h3>
                                <div className={classes.outcomesGrid}>
                                    {course.outcomes.map((o, i) => reveal(
                                        <div className={classes.outcomeCard}>
                                            <h4 className={classes.outcomeTitle}>{pick(o.title)}</h4>
                                            <p className={classes.outcomeText}>{pick(o.text)}</p>
                                        </div>,
                                        {direction: 'up', delay: i * 40, key: pick(o.title)},
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </section>

                {/* Для кого */}
                <section className={classes.sectionAlt}>
                    <div className={classes.container}>
                        <SectionTitle id="coursesPage.course.audienceTitle" className={classes.sectionHead} as="h2"/>
                        {/* В документах аудитория описана одной фразой, а не списком ролей.
                            Одну фразу показываем абзацем — карточка во всю ширину с длинным
                            предложением выглядит как ошибка вёрстки. */}
                        {(course.targetAudience || []).length === 1 ? (
                            <p className={classes.goalText} style={{marginBottom: 0}}>
                                {pick(course.targetAudience[0])}
                            </p>
                        ) : (
                            <div className={classes.rolesGrid}>
                                {(course.targetAudience || []).map((role, i) => {
                                    const Icon = ROLE_ICONS[i % ROLE_ICONS.length];
                                    return (
                                        <div className={classes.roleCard} key={pick(role)}>
                                            <Icon/>
                                            <span>{pick(role)}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <div className={classes.audienceChips}>
                            {(course.audienceTags || []).map((code) => {
                                const tag = AUDIENCE_BY_CODE[code];
                                return tag ? (
                                    <span className={classes.audienceChip} key={code}>
                                        <IntlMessage id={tag.labelKey}/>
                                    </span>
                                ) : null;
                            })}
                        </div>
                        <button type="button" className={classes.helpLink} onClick={scrollToForm}>
                            <IntlMessage id="coursesPage.course.audienceHelp"/>
                        </button>
                    </div>
                </section>

                {/* Тематический план */}
                <section className={classes.section}>
                    <div className={classes.container}>
                        <div className={classes.syllabusHead}>
                            <SectionTitle id="coursesPage.course.syllabusTitle" as="h2"/>
                            <span className={classes.syllabusSummary}>
                                {[
                                    `${(course.modules || []).length} ${pluralRu((course.modules || []).length, 'модуль', 'модуля', 'модулей')}`,
                                    hoursLabelFull(course.durationHours),
                                    pick(course.durationShape),
                                ].filter(Boolean).join(' · ')}
                            </span>
                        </div>
                        <SyllabusTable course={course}/>
                    </div>
                </section>

                <CtaStrip
                    titleId="coursesPage.course.ctaStripTitle"
                    textId="coursesPage.course.ctaStripText"
                    buttonId="coursesPage.enroll.button"
                    onClick={scrollToForm}
                />

                {/* Блок «Что вы унесёте с курса» скрыт по решению заказчика: макет
                    документа рядом с текстом читался как настоящая справка. Итоговый
                    продукт при этом остаётся в мета-плитке героя. */}
                {SHOW_COURSE_RESULT && (
                <section className={classes.sectionAlt}>
                    <div className={classes.container}>
                        <div className={classes.resultGrid}>
                            <div>
                                <SectionTitle id="coursesPage.course.resultTitle" className={classes.sectionHead} as="h2"/>
                                <p className={classes.goalText} style={{marginBottom: 0}}>{pick(course.finalProduct)}</p>
                                <ul className={classes.checkList}>
                                    {(course.outcomes || []).slice(0, 4).map((o) => (
                                        <li className={classes.checkItem} key={pick(o.title)}>
                                            <CheckCircleOutlined/>
                                            <span>{pick(o.title)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className={classes.artifact}>
                                <div className={classes.artifactHead}>
                                    <FileTextOutlined/>
                                    <span>{pick(course.title)}</span>
                                </div>
                                <div className={classes.artifactDivider}/>
                                {[100, 86, 94, 70, 100, 92, 64].map((w, i) => (
                                    <div
                                        key={`${w}-${i}`}
                                        className={`${classes.artifactLine} ${i === 0 || i === 4 ? classes.artifactLineStrong : ''}`}
                                        style={{width: `${w}%`}}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
                )}

                {/* Эффект для компании — тёмная секция, третий тон в чередовании.
                    В документах курсов этого блока нет, поэтому пока не выводится;
                    появится, как только редактор заполнит его в админке. */}
                {(course.forBusiness || []).length > 0 && (
                <section className={classes.sectionDark}>
                    <div className={classes.container}>
                        <SectionTitle id="coursesPage.course.businessTitle" className={classes.sectionHead} as="h2"/>
                        <div className={classes.bizGrid}>
                            {(course.forBusiness || []).map((item, i) => reveal(
                                <div className={classes.bizTile}>
                                    <CheckCircleOutlined className={classes.bizIcon}/>
                                    <p className={classes.bizText}>{pick(item)}</p>
                                </div>,
                                {direction: 'up', delay: i * 50, key: pick(item)},
                            ))}
                        </div>
                    </div>
                </section>
                )}

                {/* Преподаватель */}
                <section className={classes.section}>
                    <div className={classes.container}>
                        <SectionTitle id="coursesPage.course.trainerTitle" className={classes.sectionHead} as="h2"/>
                        <div className={classes.trainerBody}>
                            {photo
                                ? <img className={classes.trainerPhoto} src={photo} alt={pick(course.trainer.name)}/>
                                : <div className={classes.trainerPhotoStub}><UserOutlined/></div>}
                            <div>
                                <h3 className={classes.trainerName}>{pick(course.trainer?.name)}</h3>
                                <p className={classes.trainerPos}>{pick(course.trainer?.position)}</p>
                                <p className={classes.trainerCred}>{pick(course.trainer?.credentials)}</p>
                                <p className={classes.trainerQuote}>{pick(course.trainer?.why)}</p>
                                {course.trainer?.isPlaceholder && (
                                    <span className={classes.trainerStub}>
                                        <IntlMessage id="coursesPage.course.trainerPlaceholder"/>
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                <HowItWorks tone="alt"/>

                {/* Документ об окончании */}
                <section className={classes.section}>
                    <div className={classes.container}>
                        <div className={classes.certGrid}>
                            <div>
                                <SectionTitle id="coursesPage.course.certTitle" className={classes.sectionHead} as="h2"/>
                                <p className={classes.goalText} style={{marginBottom: 0}}>
                                    <IntlMessage id="coursesPage.course.certText"/>
                                </p>
                                <ul className={classes.checkListSingle}>
                                    {['c1', 'c2', 'c3'].map((k) => (
                                        <li className={classes.checkItem} key={k}>
                                            <CheckCircleOutlined/>
                                            <span><IntlMessage id={`coursesPage.course.cert.${k}`}/></span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <CertificatePreview course={course}/>
                        </div>
                    </div>
                </section>

                {/* Блок вопросов по курсу скрыт по решению заказчика. Данные (course.faq)
                    и компонент остаются на месте — чтобы вернуть, достаточно поднять флаг. */}
                {SHOW_COURSE_FAQ && (
                    <CourseFaq titleId="coursesPage.course.faqTitle" items={faqItems} tone="alt"/>
                )}

                {/* фоны секций чередуются строго через одну, иначе соседние блоки
                    одного цвета сливаются в одно длинное поле */}
                <CourseApplyForm
                    courses={allCourses}
                    defaultCourseSlug={course.slug}
                    anchorRef={applyRef}
                    tone="alt"
                />

                {/* Похожие программы */}
                <section className={classes.section}>
                    <div className={classes.container}>
                        <SectionTitle id="coursesPage.course.relatedTitle" className={classes.sectionHead} as="h2"/>
                        <div className={classes.relatedGrid}>
                            {related.map((c, i) => <CourseCard key={c.slug} course={c} index={i}/>)}
                        </div>
                    </div>
                </section>

                <MobileEnrollBar course={course} visible={barVisible} onApply={scrollToForm}/>
            </>
        );
    })();

    return (
        <ConfigProvider theme={{token: {colorPrimary: '#163269', borderRadius: 6, fontFamily: "'Montserrat', sans-serif"}}}>
            <div className={classes.page}>
                <Helmet>
                    <title>
                        {course ? `${pick(course.title)} — ${t('coursesPage.meta.suffix')}` : t('coursesPage.course.notFound')}
                    </title>
                    {course && <meta name="description" content={pick(course.cardDescription)}/>}
                </Helmet>

                {!reduced && course && (
                    <div className={classes.progress} style={{transform: `scaleX(${progress})`}}/>
                )}

                <Header/>
                {body}
                <Footer/>
            </div>
        </ConfigProvider>
    );
};

export default CoursePage;
